import { Component, OnDestroy, OnInit } from '@angular/core';
import { AdminService, CreateAdminUserDto, DynamoUser, ImpersonationSession } from '../../core/services/admin.service';
import { environment } from '../../../environments/environment';

export interface UserRow {
  id: string;
  name: string;
  email: string;
  initials: string;
  role: string;
  status: string;
  orgName: string;
  orgRole: string;
  joined: string;
  updatedAt: string;
  deletedAt?: string;
}

function toRow(u: DynamoUser): UserRow {
  const name = [u.givenName, u.familyName].filter(Boolean).join(' ') || u.email;
  const initials = [u.givenName?.[0], u.familyName?.[0]]
    .filter(Boolean)
    .join('')
    .toUpperCase() || u.email.slice(0, 2).toUpperCase();

  return {
    id: u.id,
    name,
    email: u.email,
    initials,
    role: u.role ?? 'member',
    status: u.status?.toLowerCase() ?? 'pending',
    orgName: u.orgName ?? '—',
    orgRole: u.orgRole ?? '—',
    joined: u.createdAt,
    updatedAt: u.updatedAt,
    deletedAt: u.deletedAt,
  };
}

type ConfirmActionType = 'suspend' | 'reactivate' | 'activate' | 'delete' | 'reset-password' | null;

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
})
export class UserManagementComponent implements OnInit, OnDestroy {
  searchQuery = '';
  statusFilter = '';
  roleFilter = '';
  showDeleted = false;

  users: UserRow[] = [];
  totalCount = 0;
  nextPageToken: string | null = null;
  loading = false;
  error: string | null = null;

  selectedUser: UserRow | null = null;
  openMenuId: string | null = null;

  showCreatePanel = false;
  createForm: CreateAdminUserDto = { email: '', givenName: '', familyName: '', temporaryPassword: '' };
  createLoading = false;
  createError: string | null = null;

  // Confirmation modal
  confirmType: ConfirmActionType = null;
  confirmUser: UserRow | null = null;
  confirming = false;

  // Toast
  toast: { type: 'success' | 'error'; message: string } | null = null;
  private toastTimer?: ReturnType<typeof setTimeout>;

  constructor(private readonly adminService: AdminService) {}

  ngOnInit(): void {
    this.loadUsers();
  }

  ngOnDestroy(): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
  }

  loadUsers(pageToken?: string): void {
    this.loading = true;
    this.error = null;
    this.adminService.listUsers(pageToken ?? undefined, this.showDeleted).subscribe({
      next: (res) => {
        this.users = pageToken
          ? [...this.users, ...res.users.map(toRow)]
          : res.users.map(toRow);
        this.totalCount = pageToken ? this.totalCount + res.count : res.count;
        this.nextPageToken = res.nextPageToken;
        this.loading = false;
      },
      error: () => {
        this.error = 'Failed to load users. Check your connection and try again.';
        this.loading = false;
      },
    });
  }

  toggleShowDeleted(): void {
    this.showDeleted = !this.showDeleted;
    this.loadUsers();
  }

  loadMore(): void {
    if (this.nextPageToken) this.loadUsers(this.nextPageToken);
  }

  get filteredUsers(): UserRow[] {
    const q = this.searchQuery.toLowerCase();
    return this.users.filter(u => {
      const matchSearch = !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
      const matchStatus = !this.statusFilter || u.status === this.statusFilter;
      const matchRole = !this.roleFilter || u.role === this.roleFilter;
      return matchSearch && matchStatus && matchRole;
    });
  }

  // ── Badges ─────────────────────────────────────────────────────────────────

  statusBadge(status: string): string {
    const map: Record<string, string> = {
      active: 'bg-emerald-100 text-emerald-700',
      pending: 'bg-amber-100 text-amber-700',
      pending_onboarding: 'bg-amber-100 text-amber-700',
      suspended: 'bg-red-100 text-red-700',
      inactive: 'bg-zinc-100 text-zinc-500',
      deleted: 'bg-zinc-100 text-zinc-400 line-through',
    };
    return map[status] ?? 'bg-zinc-100 text-zinc-500';
  }

  roleBadge(role: string): string {
    const map: Record<string, string> = {
      admin: 'bg-violet-100 text-violet-700',
      member: 'bg-blue-100 text-blue-700',
      viewer: 'bg-slate-100 text-slate-600',
    };
    return map[role] ?? 'bg-zinc-100 text-zinc-500';
  }

  // ── Drawer ─────────────────────────────────────────────────────────────────

  viewUser(user: UserRow): void {
    this.selectedUser = user;
    this.openMenuId = null;
  }

  closeDrawer(): void {
    this.selectedUser = null;
  }

  toggleMenu(id: string, event: Event): void {
    event.stopPropagation();
    this.openMenuId = this.openMenuId === id ? null : id;
  }

  closeMenu(): void {
    this.openMenuId = null;
  }

  // ── Confirmation modal ─────────────────────────────────────────────────────

  get confirmMessage(): string {
    if (!this.confirmUser || !this.confirmType) return '';
    const name = this.confirmUser.name || this.confirmUser.email;
    switch (this.confirmType) {
      case 'suspend':    return `Suspend ${name}? They will immediately lose access.`;
      case 'reactivate': return `Reactivate ${name}? They will regain access immediately.`;
      case 'activate':   return `Activate ${name}'s account? They are currently pending onboarding.`;
      case 'delete':     return `Permanently delete ${name}? This removes their Cognito account and soft-deletes the record. This cannot be easily undone.`;
      case 'reset-password': return `Send a password reset email to ${name} (${this.confirmUser.email})?`;
      default: return '';
    }
  }

  requestConfirm(type: ConfirmActionType, user: UserRow): void {
    this.confirmType = type;
    this.confirmUser = user;
    this.closeMenu();
  }

  cancelConfirm(): void {
    this.confirmType = null;
    this.confirmUser = null;
    this.confirming = false;
  }

  executeConfirm(): void {
    if (!this.confirmType || !this.confirmUser || this.confirming) return;
    const user = this.confirmUser;
    switch (this.confirmType) {
      case 'suspend':
      case 'reactivate':
        this.executeToggleStatus(user);
        break;
      case 'activate':
        this.executeActivate(user);
        break;
      case 'delete':
        this.executeSoftDelete(user);
        break;
      case 'reset-password':
        this.executeResetPassword(user);
        break;
    }
  }

  // ── Toast ──────────────────────────────────────────────────────────────────

  showToast(type: 'success' | 'error', message: string): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toast = { type, message };
    this.toastTimer = setTimeout(() => { this.toast = null; }, 4000);
  }

  dismissToast(): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toast = null;
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  activateUser(user: UserRow): void {
    this.requestConfirm('activate', user);
  }

  private executeActivate(user: UserRow): void {
    this.confirming = true;
    this.adminService.updateUser(user.id, { status: 'ACTIVE' }).subscribe({
      next: (updated) => {
        const row = toRow(updated);
        const idx = this.users.findIndex(u => u.id === user.id);
        if (idx !== -1) this.users[idx] = row;
        if (this.selectedUser?.id === user.id) this.selectedUser = row;
        this.cancelConfirm();
        this.showToast('success', `${user.name} has been activated.`);
      },
      error: () => {
        this.confirming = false;
        this.showToast('error', 'Failed to activate user.');
      },
    });
  }

  toggleStatus(user: UserRow): void {
    const action: ConfirmActionType = user.status === 'suspended' ? 'reactivate' : 'suspend';
    this.requestConfirm(action, user);
  }

  private executeToggleStatus(user: UserRow): void {
    const newStatus = user.status === 'suspended' ? 'active' : 'suspended';
    this.confirming = true;
    this.adminService.updateUser(user.id, { status: newStatus }).subscribe({
      next: (updated) => {
        const row = toRow(updated);
        const idx = this.users.findIndex(u => u.id === user.id);
        if (idx !== -1) this.users[idx] = row;
        if (this.selectedUser?.id === user.id) this.selectedUser = row;
        this.cancelConfirm();
        this.showToast('success', `${user.name} has been ${newStatus === 'active' ? 'reactivated' : 'suspended'}.`);
      },
      error: () => {
        this.confirming = false;
        this.showToast('error', 'Failed to update user status.');
      },
    });
  }

  softDelete(user: UserRow): void {
    this.requestConfirm('delete', user);
  }

  private executeSoftDelete(user: UserRow): void {
    this.confirming = true;
    this.adminService.deleteUser(user.id).subscribe({
      next: (res) => {
        if (this.showDeleted) {
          const idx = this.users.findIndex(u => u.id === user.id);
          if (idx !== -1) {
            this.users[idx] = { ...this.users[idx], status: 'deleted', deletedAt: res.deletedAt };
            if (this.selectedUser?.id === user.id) this.selectedUser = this.users[idx];
          }
        } else {
          this.users = this.users.filter(u => u.id !== user.id);
          this.totalCount = Math.max(0, this.totalCount - 1);
          if (this.selectedUser?.id === user.id) this.closeDrawer();
        }
        this.cancelConfirm();
        this.showToast('success', `${user.name} has been deleted.`);
      },
      error: () => {
        this.confirming = false;
        this.showToast('error', 'Failed to delete user.');
      },
    });
  }

  restoreUser(user: UserRow): void {
    this.adminService.restoreUser(user.id).subscribe({
      next: (updated) => {
        const row = toRow(updated);
        const idx = this.users.findIndex(u => u.id === user.id);
        if (idx !== -1) this.users[idx] = row;
        if (this.selectedUser?.id === user.id) this.selectedUser = row;
        this.closeMenu();
        this.showToast('success', `${user.name} has been restored.`);
      },
      error: () => {
        this.showToast('error', 'Failed to restore user.');
      },
    });
  }

  resetPassword(user: UserRow): void {
    this.requestConfirm('reset-password', user);
  }

  private executeResetPassword(user: UserRow): void {
    this.confirming = true;
    this.adminService.resetPassword(user.id).subscribe({
      next: () => {
        this.cancelConfirm();
        this.showToast('success', `Password reset email sent to ${user.email}.`);
      },
      error: (err: { error?: { message?: string } }) => {
        this.confirming = false;
        this.showToast('error', err?.error?.message ?? 'Failed to initiate password reset.');
      },
    });
  }

  impersonate(user: UserRow): void {
    this.closeMenu();
    this.adminService.startImpersonation(user.id).subscribe({
      next: (session: ImpersonationSession) => {
        const dashboardUrl = environment.dashboardUrl;
        window.open(`${dashboardUrl}?impersonateToken=${session.sessionToken}`, '_blank');
        this.showToast('success', `Impersonation session started for ${user.name}. Opened in new tab.`);
      },
      error: (err: { error?: { message?: string } }) => {
        this.showToast('error', err?.error?.message ?? 'Failed to start impersonation session.');
      },
    });
  }

  // ── Create admin user ──────────────────────────────────────────────────────

  openCreatePanel(): void {
    this.createForm = { email: '', givenName: '', familyName: '', temporaryPassword: '' };
    this.createError = null;
    this.showCreatePanel = true;
    this.selectedUser = null;
  }

  closeCreatePanel(): void {
    this.showCreatePanel = false;
    this.createError = null;
  }

  submitCreate(): void {
    this.createLoading = true;
    this.createError = null;
    this.adminService.createUser(this.createForm).subscribe({
      next: (user) => {
        this.users = [toRow(user), ...this.users];
        this.totalCount += 1;
        this.createLoading = false;
        this.closeCreatePanel();
        this.showToast('success', `Admin user ${user.email} created successfully.`);
      },
      error: (err: { error?: { message?: string } }) => {
        this.createError = err?.error?.message ?? 'Failed to create user.';
        this.createLoading = false;
      },
    });
  }

}

